import { apiAction, API_SUCCESS } from "../dux/api";
import {
  FETCH_BOOKING_RECORD,
  INVOICE_DOWNLOAD,
  UPDATE_PAYMENT_STATUS,
  UPDATE_PAYMENT_STATUS_SUPER,
  DOWNLOAD_BOOKING_RECORD_CSV,
  EDIT_BOOKING_RECORD,
  setBookingRecord,
  fetchBookingRecord,
} from "../dux/bookingRecords";
import { parseBookingRecords } from "../helpers/bookingRecordsHelper";
import { getBaseApiUrl } from "../helpers/generic";
import { getInvoiceDownloadUrl } from "../helpers/apiEndPoints";
import { apiCallStart, apiCallComplete } from "../dux/apiStatus";
import { openSnackbar } from "../dux/snackbar";
import { API_ERROR } from "../dux/api";

const bookingRecords =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    if (action.type === FETCH_BOOKING_RECORD) {
      next(apiCallStart({ api: "fetchBookingRecord" }));
      next(
        apiAction({
          url: `${getBaseApiUrl()}api/v1/booking/`,
          feature: FETCH_BOOKING_RECORD,
          data: {
            startDate: action.payload.startDate ?? null,
            endDate: action.payload.endDate ?? null,
            mobileNumber: action.payload.mobileNumber ?? null,
            sort_by:
              action.payload.sortField === "playingTimestamp"
                ? "playing_timestamp"
                : "booking_timestamp",
            sort_order: "ASC",
            location: action.payload.location ?? null,
            booking_id: action.payload.bookingId ?? null,
            page: action.payload.page ?? 1,
            limit: action.payload.limit ?? 20,
          },
        })
      );
    }

    if (action.type === EDIT_BOOKING_RECORD) {
      const bid = action.payload.bookingId;
      const bookingIdStr =
        bid != null && bid !== "" ? String(bid) : null;
      if (!bookingIdStr) {
        next(apiCallComplete({ api: "editBooking" }));
        next(
          openSnackbar({
            message: "Booking ID is required to edit total amount.",
            status: "ERROR",
          })
        );
        return;
      }
      next(apiCallStart({ api: "editBooking" }));
      const refresh = action.payload.refresh;
      next(
        apiAction({
          url: `${getBaseApiUrl()}api/v1/booking/edit`,
          method: "PUT",
          feature: EDIT_BOOKING_RECORD,
          data: {
            bookingId: bookingIdStr,
            total_amount: action.payload.totalAmount ?? null,
          },
          meta: {
            refresh,
            onSuccess: () => {
              if (refresh) {
                dispatch(fetchBookingRecord(refresh));
              }
            },
          },
        })
      );
    }

    if (action.type === `${EDIT_BOOKING_RECORD} ${API_SUCCESS}`) {
      next(apiCallComplete({ api: "editBooking" }));
      next(
        openSnackbar({
          message: "Booking has been updated successfully.",
        })
      );
    }

    if (action.type === `${EDIT_BOOKING_RECORD} ${API_ERROR}`) {
      next(apiCallComplete({ api: "editBooking" }));
    }

    if (action.type === INVOICE_DOWNLOAD) {
      next(
        apiAction({
          url: getInvoiceDownloadUrl(action.payload.id),
          feature: INVOICE_DOWNLOAD,
          responseType: "blob",
          headers: {
            "Content-Type": "application/json",
          },
        })
      );
    }

    if (action.type === DOWNLOAD_BOOKING_RECORD_CSV) {
      next(
        apiAction({
          url: `${getBaseApiUrl()}api/v1/booking/csv`,
          method: "GET",
          feature: DOWNLOAD_BOOKING_RECORD_CSV,
          data: {
            startDate: action.payload.startDate ?? null,
            endDate: action.payload.endDate ?? null,
            mobileNumber: action.payload.mobileNumber ?? null,
            sort_by:
              action.payload.sortField === "playingTimestamp"
                ? "playing_timestamp"
                : "booking_timestamp",
            sort_order: "ASC",
            location: action.payload.location ?? null,
          },
        })
      );
    }

    if (action.type === `${DOWNLOAD_BOOKING_RECORD_CSV} ${API_SUCCESS}`) {
      next(apiCallComplete({ api: "csvDownload" }));

      // Handle the CSV generation response and trigger download
      const response = action.payload;
      if (response.success && response.download_url) {
        // Make a second API call to download the actual file via apiMiddleware
        const baseUrl = getBaseApiUrl().replace(/\/$/, "");
        const downloadUrl = `${baseUrl}${response.download_url}`;
        next(
          apiAction({
            url: downloadUrl,
            feature: "DOWNLOAD_BOOKING_RECORD_CSV_FILE",
            method: "GET",
            responseType: "blob",
            headers: { "Content-Type": "text/csv" },
            meta: {
              onSuccess: () => {
                next(
                  openSnackbar({
                    message: "CSV downloaded successfully.",
                    status: "SUCCESS",
                  })
                );
              },
              onError: () => {
                next(
                  openSnackbar({
                    message: "Failed to download CSV file.",
                    status: "ERROR",
                  })
                );
              },
            },
          })
        );
      } else {
        next(
          openSnackbar({
            message: "Failed to generate CSV file.",
            status: "ERROR",
          })
        );
      }
    }

    if (action.type === `${DOWNLOAD_BOOKING_RECORD_CSV} ${API_ERROR}`) {
      next(apiCallComplete({ api: "csvDownload" }));
    }

    if (action.type === `${INVOICE_DOWNLOAD} ${API_SUCCESS}`) {
      next(apiCallComplete({ api: "invoiceDownload" }));
      next(
        openSnackbar({
          message: "Invoice has been download successfully.",
        })
      );
    }

    if (action.type === `${INVOICE_DOWNLOAD} ${API_ERROR}`) {
      next(apiCallComplete({ api: "invoiceDownload" }));
    }

    if (action.type === UPDATE_PAYMENT_STATUS) {
      next(
        apiAction({
          url: `${getBaseApiUrl()}api/v1/booking/change-payment-status/admin`,
          method: "POST",
          feature: UPDATE_PAYMENT_STATUS,
          data: {
            bookingId: action.payload.bookingId ?? null,
            status: action.payload.status ?? null,
          },
        })
      );
    }

    if (action.type === `${UPDATE_PAYMENT_STATUS} ${API_SUCCESS}`) {
      next(apiCallComplete({ api: "bookingStatusChange" }));
      next(
        openSnackbar({
          message: "Booking status had been changed successfully.",
        })
      );
    }

    if (action.type === `${UPDATE_PAYMENT_STATUS} ${API_ERROR}`) {
      next(apiCallComplete({ api: "bookingStatusChange" }));
    }

    if (action.type === UPDATE_PAYMENT_STATUS_SUPER) {
      next(
        apiAction({
          url: `${getBaseApiUrl()}api/v1/booking/change-payment-status/super/admin`,
          method: "POST",
          feature: UPDATE_PAYMENT_STATUS_SUPER,
          data: {
            bookingId: action.payload.bookingId ?? null,
            status: action.payload.status ?? null,
          },
        })
      );
    }

    if (action.type === `${UPDATE_PAYMENT_STATUS_SUPER} ${API_SUCCESS}`) {
      next(apiCallComplete({ api: "bookingStatusChange" }));
      next(
        openSnackbar({
          message: "Booking status had been changed successfully.",
        })
      );
    }

    if (action.type === `${UPDATE_PAYMENT_STATUS_SUPER} ${API_ERROR}`) {
      next(apiCallComplete({ api: "bookingStatusChange" }));
      next(
        openSnackbar({
          message: "Failed to update payment status.",
        })
      );
    }

    if (action.type === `${FETCH_BOOKING_RECORD} ${API_SUCCESS}`) {
      // The API middleware dispatches apiSuccess with { response: axiosResponse.data, feature, meta }
      // But the actual payload structure is { bookings: [...], total: number, page: number, limit: number }
      const bookings = action.payload.bookings || [];
      const total = action.payload.total || 0;
      const parsedData = parseBookingRecords(bookings);

      next(setBookingRecord({ list: parsedData, total }));
      next(apiCallComplete({ api: "fetchBookingRecord" }));
    }

    if (action.type === `${FETCH_BOOKING_RECORD} ${API_ERROR}`) {
      next(apiCallComplete({ api: "fetchBookingRecord" }));
    }
  };

export default bookingRecords;
